import {Injectable, isDevMode} from '@angular/core';
import {AngularFirestore, } from "@angular/fire/compat/firestore";
import {distinctUntilChanged, map, shareReplay, take, tap} from "rxjs/operators";
import {AngularFireDatabase} from "@angular/fire/compat/database";
import {FormArray, FormGroup} from "@angular/forms";
import {FullChild} from "../../interfaces/FullChild";
import firebase from "firebase/compat/app";
import {environment} from "../../../environments/environment";
import {combineLatest, concat, filter, Observable} from "rxjs";
import dateutil from "rrule/dist/esm/src/dateutil";
import sort = dateutil.sort;
import {AdmissionLogService} from "../../services/admission-log.service";
import {collection as fsCollection, getFirestore, onSnapshot, QuerySnapshot} from "@angular/fire/firestore";
import {UnitError} from "../../services/unit.service";

/** The child code is already used by another child; the admission was not saved. */
export class ChildCodeTakenError extends UnitError {
    constructor(childCode: string) {
        super(`Child code ${childCode} is already used by another child, so the admission was not saved. Press Save again to get a new code.`);
        this.name = 'ChildCodeTakenError';
    }
}


@Injectable({
    providedIn: 'root'
})
export class ChildService {
    env = isDevMode() ? environment.testMode : environment.productionMode
    readCount = 0
    userData = JSON.parse(localStorage.getItem('userData')!)

    constructor(private firestore: AngularFirestore,
                private realtimeDB: AngularFireDatabase,
                private admissionLogService: AdmissionLogService) {
    }
    getCurrentChildCounter() {
        return this.realtimeDB.object(`${this.env.counters}/childCounter`).valueChanges()
    }

    /** Counter value -> child code, e.g. 1234 -> AL-2026-01234 */
    formatChildCode(counter: number | string) {
        return `AL-${new Date().getFullYear()}-${String(counter).padStart(5, '0')}`
    }

    /**
     * Takes the next child code atomically: the counter holds the next free number, and a transaction
     * returns it and increments it in one step, so two admissions can never get the same code.
     * Codes that already have a child (counter fell behind in the past) are skipped.
     */
    async reserveChildCode(): Promise<string> {
        const counterRef = this.realtimeDB.database.ref(`${this.env.counters}/childCounter`)
        for (let attempt = 0; attempt < 10; attempt++) {
            const result = await counterRef.transaction(current => (Number(current) || 0) + 1)
            if (!result.committed) break
            const childCode = this.formatChildCode(Number(result.snapshot.val()) - 1)
            const existing = await this.firestore.firestore.doc(`${this.env.child}/${childCode}`).get()
            if (!existing.exists) return childCode
            console.warn(`Child code ${childCode} already has a child; skipping it`)
        }
        throw new UnitError('Could not get a new child code. Please try again, and contact support if it keeps happening.')
    }

    addGuardian(childCode: string, guardian: FormArray) {
        return this.firestore.doc(`${this.env.caretakers}/caretakers`).set({[childCode]: guardian.value})
    }

//** TO GET PRESCREENING CHILD
    fetchPreScreeningChild() {
        return this.firestore.doc(this.env.prescreening).get()
    }

    getChildList() {
        const childDoc = this.firestore.doc(this.env.prescreening)
        return childDoc.snapshotChanges();
    }

    //?MIS
    getRegisteredChildList() {
        return this.firestore.collection<FullChild>(this.env.child).get().pipe(
            shareReplay(1)
        )
        // return this.firestore.collection<FullChild>('ALChild').get().pipe(
        //     shareReplay(1)
        // )
    }

    getRegisteredChildListByDateRange(startDate: Date, endDate: Date) {
        return this.firestore.collection<FullChild>(this.env.child, ref => ref.where('dateOfAdmission', '>=', startDate)
            .where('dateOfAdmission', '<=', endDate)).get().pipe(
            shareReplay(1)
        )
        // return this.firestore.collection<FullChild>('ALChild').get().pipe(
        //     shareReplay(1)
        // )
    }

    getRegisteredChildListByDateRangeAndCentre(centreCode: string, startDate: Date, endDate: Date) {
        return this.firestore.collection<FullChild>(this.env.child, ref => ref
            .where('centreCode', '==', centreCode)
            .where('dateOfAdmission', '>=', startDate)
            .where('dateOfAdmission', '<=', endDate)).get().pipe(
            shareReplay(1)
        )
        // return this.firestore.collection<FullChild>('ALChild').get().pipe(
        //     shareReplay(1)
        // )
    }

    //? MIS
    getRegisteredChildListByCentre(centreCode: string) {
        return this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode)).get().pipe(
            shareReplay(1)
        )
        // return this.firestore.collection<FullChild>('ALChild').get().pipe(
        //     shareReplay(1)
        // )
    }


    /**
     * Same data as getLatestData(), but as a listener rather than a one-shot get.
     *
     * A get() is billed for every matching document on every call. A listener over a
     * persisted cache resumes from a stored token, so repeat visits are billed only
     * for the documents that changed since the last sync — which for this collection
     * is the difference between thousands of reads and a handful.
     *
     * Kept separate from getLatestData() on purpose: that method is shared by seven
     * components which treat it as one-shot, and a live stream would re-run their
     * processing on every child edit.
     *
     * Emits a QuerySnapshot, the same shape getLatestData() returns, so callers keep
     * using `.docs`. Uses the modular SDK so it runs on the instance persistence is
     * enabled on in app.module.ts.
     */
    private allChildren$?: Observable<QuerySnapshot<FullChild>>;

    watchAllChildren(): Observable<QuerySnapshot<FullChild>> {
        if (!this.allChildren$) {
            this.allChildren$ = new Observable<QuerySnapshot<FullChild>>(subscriber =>
                onSnapshot(
                    fsCollection(getFirestore(), this.env.child),
                    snapshot => subscriber.next(snapshot as QuerySnapshot<FullChild>),
                    error => subscriber.error(error)
                )
            ).pipe(
                // refCount stays false: the cache must outlive the last unsubscribe, or
                // navigating away and back would pay for the whole collection again.
                shareReplay({bufferSize: 1, refCount: false})
            );
        }
        return this.allChildren$;
    }

//? MIS FINANCIAl REPORT
    getLatestData() {
        return this.firestore.collection<FullChild>(this.env.child).get().pipe(
            tap(arr => console.log(`centre: read (${this.readCount += arr.docs.length} )docs`)),
            shareReplay(1)
        )
    }
  getBorivaliData() {
    return this.firestore.collection<FullChild>(this.env.child,ref => ref.where('centreCode','==','ALBorivali')).get().pipe(
      tap(arr => console.log(`centre: read (${this.readCount += arr.docs.length} )docs`)),
      shareReplay(1)
    )
  }
    // getChildData():Observable<ChildData>{
    //   const childDoc= this.firestore.doc<ChildData>('testchild/testdocument')
    // return childDoc.snapshotChanges().pipe(map(changes=>{
    //   const data = changes.payload.data();
    //   const id  = changes.payload.id;
    //   // @ts-ignore
    //   return {id, ...data};
    // }));
    // }
    addTempChild(value: any) {
        this.firestore.collection(this.env.prescreening).add(value).then(() => {
            console.log('data sent successfully')
        })
            .catch((reason) => {
                console.log('error sending data' + reason)
            })
    }

    updatePrescreening(value: any) {
        this.firestore.collection(this.env.prescreening).doc(value.id).update(value)
            .then(() => {
                console.log('data updated successfully')
            })
            .catch((reason) => {
                console.log('error sending data' + reason)
            })
    }

    //* REGISTER CHILD
    enrollChild(childCode: string, form: FormGroup) {
        console.log('INTO ENROLL CHILD', childCode)
        const childRef = this.firestore.collection(this.env.child).doc(childCode).ref
        const data = {
            'dateOfAdmission': form.get('dateOfAdmission')?.value,
            'type': 'Admission',
            'currentUnit': form.get('selectUnit')?.value,
            'centreCode': form.get('centreCode')?.value,
            'centreName': form.get('centreName')?.value
        }
        // Create only: never overwrite an existing child with the same code
        return this.firestore.firestore.runTransaction(async tx => {
            if ((await tx.get(childRef)).exists) throw new ChildCodeTakenError(childCode)
            tx.set(childRef, {...form.value, admissionData: [data]})
        }).then(() => {
            this.admissionLogService.createUniqueAdmissionLogObject(form.value,'Admission',this.userData.email)
                .then(() => {
                    console.log('admission log created successfully')
                })
                .catch((reason) => {
                    console.log('error adding admission Log data' + reason)
                })
            this.admissionLogService.createCycleOnAdmission({...form.value, childCode}, this.userData.email)
                .catch(err => console.error('Failed to create admission cycle doc:', err))
        })
    }

    fetchTestChildList() {
        const childDoc = this.firestore.collection<FullChild>(this.env.prescreening,
            ref => ref.orderBy('createdAt', 'desc'))
        return childDoc.snapshotChanges();

    }

//* Fetch All children from all centres in  firestore in registration child assessment-table all
    childRegistration() {
        const childDoc = this.firestore.collection<FullChild>(this.env.child)

        // @ts-ignore
        return childDoc.valueChanges({idField: 'id'}).pipe(
            take(1),
            tap(arr => {
                console.log(`read ${arr.length} docs`)
                console.log('ChildRegistration', this.env.child)
            }),
            shareReplay(1)
        );

    }

    childRegistrationbyCenter(centreCode: string) {
        console.log('INTO', 'childRegistrationbtCenter')
        // let startDate = new Date('2016-03-31')
        // let endDate = new Date('2017-04-01')
        // // alert( 'full year is : '+d)
        // console.log('NEW START DATE IS ', +startDate)
        // console.log('NEW END DATE IS ', +endDate)
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode)
            // .where('dateOfAdmission','<=',startDate).where('dateOfAdmission','<=',endDate)
        )
        console.log(childDoc)
        // @ts-ignore
        return childDoc.snapshotChanges().pipe(
            take(1),
            tap(arr => {
                    console.log(`read ${arr.length} docs`)
                    console.log('Child Registration By Center', this.env.child)
                }
            ), shareReplay(1)
        );
    }

    getChildListByCenter(centreCode: string) {
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode)
          .orderBy('firstName', 'asc')
        )
        // @ts-ignore
        return childDoc.valueChanges({idField: 'id'}).pipe(take(1))

    }

  getAllotedChildListByCenter(centreCode: string): Observable<FullChild[]> {
    const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true));
    return childDoc.valueChanges({ idField: 'id' }).pipe(
      take(1),
      map(children => children.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName)))
    );
  }
  getAllotedChildListByCenterName(centreName: string): Observable<FullChild[]> {
    const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreName', '==', centreName).where('age','>=',3).where('isAlloted', '==', true));
    return childDoc.valueChanges({ idField: 'id' }).pipe(
      take(1),
      map(children => children.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName)))
    );
  }
    fetchAllotedChildListByCenter(centreCode: string) {
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.orderBy('firstName')
            .where('centreCode', '==', centreCode).where('isAlloted', '==', true))
        // @ts-ignore
        return childDoc.get({idField: 'id'})
    }
  getAlloteCaseStudydChildListByCenter(centreCode: string) {
    const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true))
    return childDoc.valueChanges({idField: 'id'}).pipe(
      take(1),
      map(children => children.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName)))
    )
  }

    getAllotedChildListByCenterAboveThree(centreCode: string) {
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true).where('age', '>=', 3))
        return childDoc.valueChanges({idField: 'id'}).pipe(
          take(1),
          map(children => children.sort((a, b) => a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName)))
        )
    }

    getAllotedChildListByCentreLiveAgeAbove3(centreCode: string) {
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true))
        // @ts-ignore
        return childDoc.valueChanges({idField: 'id'}).pipe(take(1))
    }

    getDischargedChildListByCenterAboveThreeWithinThreeDaysEducation(centreCode: string) {

        const currentDate = new Date();
        const currentDate2 = new Date();
        currentDate2.setDate(currentDate2.getDate()-30);
        // *Convert the date to Firestore Timestamp
        const timestamp = firebase.firestore.Timestamp.fromDate(currentDate2);
        const currentTimestamp = firebase.firestore.Timestamp.fromDate(currentDate);

        return this.firestore.collection<FullChild>(this.env.child).ref
            .where('centreCode','==',centreCode)
            .where('isAlloted', '==', false)
            .where('dischargeDate', '>=', timestamp)
            .where('dischargeDate', '<=', currentTimestamp)
            .get()



    }



    //? Used in Educatiom Group Component
    getAllotedChildListByCenterArrayAboveThree(centreCodes: string[]) {
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', 'in', centreCodes).where('isAlloted', '==', true).where('age', '>=', 3))
        // @ts-ignore
        return childDoc.valueChanges({idField: 'id'}).pipe(take(1))
    }

    editChild(childCode
                  :
                  string | undefined, value
                  :
                  any
    ) {
        return this.firestore.collection(this.env.child).doc(childCode).update(value)
        // return this.firestore.collection(this.env.child).doc(childCode).update(value)
    }

    getAllotedChildListByCenterarray(centreCodes: String[]) {
        const childDoc = this.firestore.collection<FullChild>(this.env.child, ref => ref.orderBy('firstName','asc').where('centreCode', 'in', centreCodes).where('isAlloted', '==', true))
        return childDoc.valueChanges({idField: 'id'}).pipe(take(1))
    }

    //* Used in child registration Table For Alloted , Discharged and All Children
    allotedChildRegistrationbyCenter(selectedStatus: string, centreCode: string) {
        switch (selectedStatus) {
            case 'Staying': {
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
            case 'Discharged': {
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', false))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
            case 'All': {
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
            default: {
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
        }
    }

    allotedChildRegistrationbyCenterCombineLatest(selectedStatus: string, centreCode: string) {
        switch (selectedStatus) {
            case 'Staying': {
                console.log('INTO', 'allotedChildRegistrationbyCenterCombineLatest CASE: Staying')
                const centreQuery = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true)).valueChanges().pipe(take(1), shareReplay(1))
                const baseCenterQuery = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('baseCentreCode', '==', centreCode).where('isAlloted', '==', true)).valueChanges().pipe(take(1), shareReplay(1))

                return combineLatest(centreQuery, baseCenterQuery).pipe(
                    map(([burgers, donuts]) => removeDuplicates([...burgers, ...donuts])),
                    shareReplay(1))
            }
          case 'OM SHANTI': {
              console.log('INTO', 'allotedChildRegistrationbyCenterCombineLatest CASE: OM SHANTI')
            const centreQuery = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('expired', '==', true)).valueChanges().pipe(take(1), shareReplay(1))
            const baseCenterQuery = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('baseCentreCode', '==', centreCode).where('expired', '==', true)).valueChanges().pipe(take(1), shareReplay(1))

            return combineLatest(centreQuery, baseCenterQuery).pipe(
              map(([burgers, donuts]) => removeDuplicates([...burgers, ...donuts])),
              shareReplay(1))
          }
            case 'Discharged': {
                console.log('INTO', 'allotedChildRegistrationbyCenterCombineLatest CASE: Discharged')
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', false))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
            case 'All': {
                console.log('INTO', 'allotedChildRegistrationbyCenterCombineLatest CASE: All')
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
            default: {
                console.log('INTO', 'allotedChildRegistrationbyCenterCombineLatest CASE: Default')
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
        }
    }

    //* Duplicate record issue fixed in this one
    allotedChildRegistrationbyCenterCombineLatest2(selectedStatus: string, centreCode: string) {
        switch (selectedStatus) {
            case 'Staying': {
                const centreQuery$ = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', true)).valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
                const baseCenterQuery$ = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('baseCentreCode', '==', centreCode).where('isAlloted', '==', true)).valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))

                return combineLatest(centreQuery$, baseCenterQuery$).pipe(
                    map(([q1, q2]) => dedupeById([...q1, ...q2])),
                    shareReplay(1))
            }
          case 'OM SHANTI': {
            const centreQuery$ = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('status', '==', "OM SHANTI")).valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            const baseCenterQuery$ = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('baseCentreCode', '==', centreCode).where('status', '==', "OM SHANTI")).valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))

            return combineLatest(centreQuery$, baseCenterQuery$).pipe(
              map(([q1, q2]) => dedupeById([...q1, ...q2])),
              shareReplay(1))
          }

            case 'Discharged': {
                const centreQuery$ = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('isAlloted', '==', false)).valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
                const baseCenterQuery$ = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('baseCentreCode', '==', centreCode).where('isAlloted', '==', false)).valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
                return combineLatest(centreQuery$, baseCenterQuery$).pipe(
                    map(([q1, q2]) => dedupeById([...q1, ...q2])),
                    shareReplay(1))
            }
            case 'All': {
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
            default: {
                const query = this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode))
                // @ts-ignore
                return query.valueChanges({idField: 'id'}).pipe(take(1), shareReplay(1))
            }
        }
    }


    getRegisteredChildListByCentreandDateRange(centreCode: string, mStartDate: Date, endDate: Date) {
        return this.firestore.collection<FullChild>(this.env.child, ref => ref.where('centreCode', '==', centreCode).where('dateOfAdmission', '>=', mStartDate).where('dateOfAdmission', '<=', endDate)).get().pipe(
            shareReplay(1))
    }

    deletePrescreening(id: string) {
        return this.firestore.collection(this.env.prescreening).doc(id).delete()

    }

    getCalculatedAge(date: firebase.firestore.Timestamp) {
        let timeDiff = Math.abs(Date.now() - (date).toDate().getTime());
        let age = Math.floor((timeDiff / (1000 * 3600 * 24)) / 365.25);
        // * UPDATING AGE IN FIRESTORE ALCHILD
        // this.mFirestore.collection('ALChild').doc(childCode).update({age: age}).then(() => {
        //     console.log(`SET DATE TO ${childCode} as`, age)
        // })
        //     .catch(() => {
        //         console.log('ERROR ADDING AGE IN', childCode)
        //     })
        console.log(age)
        return age
    }

    performMergeQuery(centreQuery$: Observable<FullChild[]>, baseCenterQuery$: Observable<FullChild[]>): Observable<FullChild[]> {
        // const collectionRef1: AngularFirestoreCollection<any> = this.firestore.collection('collection1');
        // const collectionRef2: AngularFirestoreCollection<any> = this.firestore.collection('collection2');
        //
        // const query1$: Observable<FullChild[]> = collectionRef1.valueChanges();
        // const query2$: Observable<FullChild[]> = collectionRef2.valueChanges();

        return concat(centreQuery$, baseCenterQuery$).pipe(
            distinctUntilChanged(),
            map(queryResults => {
                const uniqueResults = [];
                const idsSet = new Set();

                for (const result of queryResults) {
                    const id = result.id;

                    if (!idsSet.has(id)) {
                        idsSet.add(id);
                        uniqueResults.push(result);
                    }
                }

                return uniqueResults;
            })
        );
    }

    findChildByCode(childCode: string) {
        return this.firestore.firestore.collection(this.env.child).doc(childCode).get();
    }

    getDischargeChildsBeforeThreeDaysFromCurrentDate() {
        const currentDate = new Date();
        const currentDate2 = new Date();
        currentDate2.setDate(currentDate2.getDate()-3);
        // *Convert the date to Firestore Timestamp
        const timestamp = firebase.firestore.Timestamp.fromDate(currentDate2);
        const currentTimestamp = firebase.firestore.Timestamp.fromDate(currentDate);
        return this.firestore.collection(this.env.child).ref
            .where('isAlloted', '==', false)
            .where('dischargeDate', '>=', timestamp)
            .where('dischargeDate', '<=', currentTimestamp)
            .get()

        // .where('admissionData.')

    }

    getDischargeChildsBeforeThreeDaysFromCurrentDateByCentre(centreCode: string) {
        const currentDate = new Date();
        let   currentDate2 = new Date();
              currentDate2.setDate(currentDate2.getDate()-40);
        // *Convert the date to Firestore Timestamp
        const timestamp = firebase.firestore.Timestamp.fromDate(currentDate2);
        const currentTimestamp = firebase.firestore.Timestamp.fromDate(currentDate);
        return this.firestore.collection<FullChild>(this.env.child).ref
            .where('centreCode','==',centreCode)
            .where('isAlloted', '==', false)
            .where('dischargeDate', '>=', timestamp.toDate())
            .where('dischargeDate', '<=', currentTimestamp.toDate())
            .get()
    }

    getDischargeChildsBeforeThreeDaysFromCurrentDateByCentreArray(centreCodes: string[]) {
        const currentDate = new Date();
        let   currentDate2 = new Date();
              currentDate2.setDate(currentDate2.getDate()-40);
        // *Convert the date to Firestore Timestamp
        const timestamp = firebase.firestore.Timestamp.fromDate(currentDate2);
        const currentTimestamp = firebase.firestore.Timestamp.fromDate(currentDate);
        return this.firestore.collection<FullChild>(this.env.child).ref
            .where('centreCode','in',centreCodes)
            .where('isAlloted', '==', false)
            .where('dischargeDate', '>=', timestamp.toDate())
            .where('dischargeDate', '<=', currentTimestamp.toDate())
            .get()
    }
    getAllAllootedChild(){
        return this.firestore.firestore.collection(this.env.child).where('isAlloted','==',true).get()
    }
  getAllAllootedChildByCentre(centreCode:string){
    let query= this.firestore.firestore.collection(this.env.child).where('isAlloted','==',true).get()
if(centreCode!='All')query= this.firestore.firestore.collection(this.env.child).where('isAlloted','==',true).where('centreCode','==',centreCode)
  .get()
    return query
  }



getNonSponsoredAllotedChildren(centreCode:string){
  let query= this.firestore.firestore.collection(this.env.child).where('isAlloted','==',true)
    .where('sponsored','!=',true)
    .get()
  if(centreCode!='All')query= this.firestore.firestore.collection(this.env.child).where('sponsored','==',true).where('centreCode','==',centreCode).get()
  return query
}
  getSponsoredAllotedChildren(centreCode:string){
    let query= this.firestore.firestore.collection(this.env.child)
      .where('centreCode','==',centreCode)
      .where('isAlloted','==',true)
      .where('sponsored','!=',true)
      .get()
    if(centreCode!='All')query= this.firestore.firestore.collection(this.env.child).where('sponsored','==',true).where('centreCode','==',centreCode).get()
    return query
  }


    private calculateLatestAge(dob: Date): number {
        const today = new Date();
        const birthDate = new Date(dob);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }
        return age;
    }


    getChildByCode(fromChild: string) {
        console.log('getChildByCode (ChildService)',fromChild)
        return   this.firestore.collection<FullChild>(this.env.child).doc(fromChild).get().toPromise()


    }

  getAllChild() {
        return this.firestore.collection<FullChild>(this.env.child).valueChanges({idField:'id'}).pipe(take(1))
    }

    getChildrenByStatus(selectedStatus: string) {
        return undefined;
    }

   //  getChildrenByCentreAndStatus(selectedCentreCode: string, selectedStatus: string) {
   //    alert(`${selectedCentreCode} ${selectedStatus}`)
   //
   // if(selectedCentreCode=='All'){
   //     switch (selectedStatus){
   //         case 'All':{
   //             return this.firestore.collection<FullChild>(this.env.child).get().pipe(
   //                 shareReplay(1)
   //             )
   //         }
   //         case '':{
   //
   //         }
   //     }
   // }
   //  }
}





//? Function to combine two arrays removing duplicate elements
function removeDuplicates(arr: FullChild[]) {
    return Array.from(new Set(arr));
}

function dedupeById(arr: (FullChild & { id?: string })[]) {
    const seen = new Set<string>();
    return arr.filter(item => {
        const key = item.id ?? item.childCode;
        if (seen.has(key)) return false;
        seen.add(key);
        if (item.baseCentreCode == null) item.baseCentreCode = item.centreCode;
        return true;
    });
}

